import { useCallback, useEffect, useRef, useState } from "react";

export interface PaneModel {
  id: string;
  tabIds: string[];
  activeTabId?: string;
}

export interface PanesLayoutState {
  panes: PaneModel[];
  sizes: number[]; // percentages summing to ~100
}

export interface UsePanesStateOptions {
  storageKey: string; // include workproductId for isolation
  initialTabs: string[]; // default initial tabs for the first pane
}

export function usePanesState(options: UsePanesStateOptions) {
  const { storageKey, initialTabs } = options;

  // Track tabs that were explicitly removed via removeTab().
  // This prevents ensureTabsExist from re-adding them as "missing"
  // during the brief window where allTabIds still includes the deleted
  // tab (context update hasn't propagated yet).
  const removedTabsRef = useRef<Set<string>>(new Set());

  const [layout, setLayout] = useState<PanesLayoutState>(() => {
    try {
      const raw = localStorage.getItem(storageKey);
      if (raw) {
        const parsed = JSON.parse(raw) as PanesLayoutState;
        if (Array.isArray(parsed?.panes) && Array.isArray(parsed?.sizes)) {
          return parsed;
        }
      }
    } catch (_) {}
    return {
      panes: [
        {
          id: createId(),
          tabIds: initialTabs.slice(),
          activeTabId: initialTabs[0],
        },
      ],
      sizes: [100],
    };
  });

  const persistRef = useRef(layout);
  persistRef.current = layout;

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(persistRef.current));
    } catch (_) {}
  }, [layout, storageKey]);

  const setSizes = useCallback((sizes: number[]) => {
    // guard: sizes length must match pane count
    setLayout(prev => {
      if (!Array.isArray(sizes) || sizes.length !== prev.panes.length) {
        const even = distributeSizesEvenly(prev.panes.length);
        return { ...prev, sizes: even };
      }
      return { ...prev, sizes };
    });
  }, []);

  const setActiveTab = useCallback((paneId: string, tabId: string) => {
    setLayout(prev => ({
      ...prev,
      panes: prev.panes.map(p =>
        p.id === paneId ? { ...p, activeTabId: tabId } : p
      ),
    }));
  }, []);

  const splitTabToNewPane = useCallback((paneId: string, tabId: string) => {
    setLayout(prev => {
      const fromIdx = prev.panes.findIndex(p => p.id === paneId);
      if (fromIdx < 0) return prev;
      const from = prev.panes[fromIdx];
      if (!from.tabIds.includes(tabId)) return prev;
      const newFromTabs = from.tabIds.filter(id => id !== tabId);
      const newPane: PaneModel = {
        id: createId(),
        tabIds: [tabId],
        activeTabId: tabId,
      };
      const newPanes = prev.panes
        .slice(0, fromIdx + 1)
        .concat(newPane, prev.panes.slice(fromIdx + 1));
      const total = prev.sizes.reduce((a, b) => a + b, 0) || 100;
      const _insertSize = Math.max(
        10,
        Math.round(total / (prev.panes.length + 1))
      );
      const sizes = distributeSizesEvenly(newPanes.length);
      const updatedFrom: PaneModel = {
        ...from,
        tabIds: newFromTabs,
        activeTabId: newFromTabs[0] || undefined,
      };
      const panes = newPanes.map(p => (p.id === from.id ? updatedFrom : p));
      return { panes, sizes };
    });
  }, []);

  const moveTab = useCallback(
    (
      tabId: string,
      fromPaneId: string,
      toPaneId: string,
      toIndex: number | "end" = "end"
    ) => {
      setLayout(prev => {
        if (fromPaneId === toPaneId) {
          // reorder within same pane
          const panes = prev.panes.map(p => {
            if (p.id !== fromPaneId) return p;
            const ids = p.tabIds.filter(id => id !== tabId);
            const idx =
              toIndex === "end"
                ? ids.length
                : Math.max(0, Math.min(ids.length, toIndex));
            ids.splice(idx, 0, tabId);
            return { ...p, tabIds: ids };
          });
          return { ...prev, panes };
        }
        // move across panes
        const panes = prev.panes.map(p => ({ ...p }));
        const from = panes.find(p => p.id === fromPaneId);
        const to = panes.find(p => p.id === toPaneId);
        if (!from || !to) return prev;
        from.tabIds = from.tabIds.filter(id => id !== tabId);
        const idx =
          toIndex === "end"
            ? to.tabIds.length
            : Math.max(0, Math.min(to.tabIds.length, toIndex));
        const newTo = to.tabIds.slice();
        newTo.splice(idx, 0, tabId);
        to.tabIds = newTo;
        if (!to.activeTabId) to.activeTabId = tabId;
        if (from.activeTabId === tabId) {
          from.activeTabId = from.tabIds[0];
        }
        // auto-remove empty pane
        const filtered = panes.filter(p => p.tabIds.length > 0);
        const sizes =
          filtered.length === panes.length
            ? prev.sizes
            : distributeSizesEvenly(filtered.length);
        return { panes: filtered, sizes };
      });
    },
    []
  );

  const closePane = useCallback((paneId: string) => {
    setLayout(prev => {
      const idx = prev.panes.findIndex(p => p.id === paneId);
      if (idx < 0) return prev;
      const panes = prev.panes.slice();
      const removed = panes.splice(idx, 1)[0];
      if (panes.length === 0) {
        // fallback to single empty pane
        return {
          panes: [
            {
              id: createId(),
              tabIds: removed.tabIds.slice(),
              activeTabId: removed.tabIds[0],
            },
          ],
          sizes: [100],
        };
      }
      // move tabs to previous or next pane
      const target = panes[idx - 1] || panes[idx];
      target.tabIds = target.tabIds.concat(removed.tabIds);
      if (!target.activeTabId && target.tabIds.length > 0)
        target.activeTabId = target.tabIds[0];
      const sizes = distributeSizesEvenly(panes.length);
      return { panes, sizes };
    });
  }, []);

  const ensureTabsExist = useCallback((allTabIds: string[]) => {
    // Remove any tabs that no longer exist; add new tabs to the first pane
    setLayout(prev => {
      const known = new Set(allTabIds);

      // Clean up removedTabsRef: once a tab is no longer in allTabIds
      // (context caught up), stop blocking it so future re-additions work.
      removedTabsRef.current.forEach(id => {
        if (!known.has(id)) removedTabsRef.current.delete(id);
      });

      const panes = prev.panes.map(p => {
        const filteredIds = p.tabIds.filter(id => known.has(id));
        const preservedActive =
          p.activeTabId && known.has(p.activeTabId) ? p.activeTabId : undefined;
        const nextActive = preservedActive || filteredIds[0];
        return {
          ...p,
          tabIds: filteredIds,
          activeTabId: nextActive,
        };
      });

      // Only add genuinely new tabs — skip any that were explicitly
      // removed via removeTab() but haven't left allTabIds yet.
      const missing = allTabIds.filter(
        id =>
          !panes.some(p => p.tabIds.includes(id)) &&
          !removedTabsRef.current.has(id)
      );
      if (missing.length > 0) {
        if (panes.length === 0) {
          panes.push({
            id: createId(),
            tabIds: missing.slice(),
            activeTabId: missing[0],
          });
        } else {
          panes[0].tabIds = panes[0].tabIds.concat(missing);
          if (!panes[0].activeTabId) panes[0].activeTabId = panes[0].tabIds[0];
        }
      }
      return { ...prev, panes };
    });
  }, []);

  // Explicitly remove a tab from all panes
  const removeTab = useCallback((tabId: string) => {
    // Mark as explicitly removed so ensureTabsExist won't re-add it
    removedTabsRef.current.add(tabId);
    setLayout(prev => {
      const panes = prev.panes.map(p => {
        const filteredIds = p.tabIds.filter(id => id !== tabId);
        const needsNewActive = p.activeTabId === tabId;
        return {
          ...p,
          tabIds: filteredIds,
          activeTabId: needsNewActive ? filteredIds[0] : p.activeTabId,
        };
      });
      // Remove any empty panes (except keep at least one)
      const nonEmpty = panes.filter(p => p.tabIds.length > 0);
      if (nonEmpty.length === 0 && panes.length > 0) {
        // Keep the first pane but empty
        return {
          ...prev,
          panes: [{ ...panes[0], tabIds: [], activeTabId: undefined }],
          sizes: [100],
        };
      }
      const sizes =
        nonEmpty.length === prev.panes.length
          ? prev.sizes
          : distributeSizesEvenly(nonEmpty.length);
      return { panes: nonEmpty.length > 0 ? nonEmpty : panes, sizes };
    });
  }, []);

  return {
    layout,
    setSizes,
    setActiveTab,
    splitTabToNewPane,
    moveTab,
    closePane,
    ensureTabsExist,
    removeTab,
  };
}

function createId() {
  const g = globalThis as any;
  if (g?.crypto?.randomUUID) return g.crypto.randomUUID();
  return `pane_${Math.random().toString(36).slice(2)}`;
}

function distributeSizesEvenly(count: number): number[] {
  if (count <= 0) return [];
  const size = Math.round(100 / count);
  const sizes = Array(count).fill(size);
  const sum = sizes.reduce((a: number, b: number) => a + b, 0);
  if (sum !== 100) sizes[0] += 100 - sum;
  return sizes;
}
